import { NUMBER_REGEX } from "@/config/constants";
import { ANSWER_TYPE_VALUES } from "@/config/dropdownData";
import { z } from "zod";
import { experienceMaxSchema, experienceMinSchema, validateJobExperienceRange } from "./experience.schema";
import { validateJobShiftSelection } from "./shift.schema";
import { languagePairRowsSchema } from "@/utils/languageRequirements";

export const jobFormDefaultValues = {
  // Basic Info
  jobTitle: "",
  deadline: "",
  location: "",
  // Employee Type and Job Priority are no longer mandatory on the form —
  // defaulted instead, so a recruiter who never touches them still submits a
  // job the backend accepts (both are required there). Vacancy Count stays
  // mandatory; its default just prefills the number field.
  employeeType: "FULL_TIME",
  shiftType: "",
  predefinedShiftId: "",
  shiftStartTime: "",
  shiftEndTime: "",
  shiftTimezone: "",
  calendarTypeId: "",
  currency: "",
  currency_sign: "",
  statusFlowId: "",

  vacancyCount: "1",
  jobPriority: "NORMAL",
  experienceMin: "",
  experienceMax: "",

  // Skills
  skills: [],

  // Description
  overview: "",
  responsibility: "",

  // Interviewers
  interviewers: [
    {
      name: "",
      email: "",
    },
  ],

  // Organization
  department: "",
  division: "",

  // Company Links
  companyWebsite: "",
  linkedIn: "",

  // Additional Info
  niceToHave: "",
  certifications: "",
  languagesRequired: [],

  // Field Config
  mandatoryFields: [],
  selectedConfigurable: [],
  selectedInterviewTypes: [],
  selectedBooleanPlatforms: [],

  // Manual screening questions — part of the Job feature, opt-in per job.
  applicationQuestions: [],
};

export const LINKEDIN_REGEX = /^(https?:\/\/)?([a-z]{2,3}\.)?(www\.)?linkedin\.com\/[^\s]+$/i;

// Whether this field value IS the job's stored deadline, compared as the same
// instant so a zone-formatted round trip of the same value still matches.
const isTheStoredDeadline = (value, originalDeadline) => {
  if (!originalDeadline) return false;
  const stored = new Date(originalDeadline).getTime();
  const sent = new Date(value).getTime();
  return Number.isFinite(stored) && stored === sent;
};

/**
 * The job form's schema, told which deadline the job already holds.
 *
 * `originalDeadline` is exempt from the future-date rule: the edit form loads
 * the stored value into the field and submits it untouched, so applying the
 * rule to it refused every edit of an expired or filled job over a field the
 * recruiter never opened. The server exempts the same value — see
 * `_deadline_for_edit` in server/app/api/v2/job/service.py. Pass nothing on
 * create, where every deadline is new.
 */
export const jobFormSchemaFor = (originalDeadline = null) => z.object({
  // Basic Info
  jobTitle: z.string().min(1, "Job title is required").min(2, "Job title must be at least 2 characters").max(150, "Job title must not exceed 150 characters"),

  deadline: z
    .string()
    .min(1, "Application due date is required")
    .refine((val) => !isNaN(Date.parse(val)), {
      message: "Please enter a valid date",
    })
    .refine((val) => isTheStoredDeadline(val, originalDeadline) || new Date(val) > new Date(), {
      message: "Deadline must be a future date",
    }),

  location: z.string().min(1, "Location is required").min(2, "Location must be at least 2 characters").max(150, "Location must not exceed 150 characters"),

  // Not mandatory — see the defaults in jobFormDefaultValues above, which is
  // what actually reaches the backend's required employee_type/vacancy_count/
  // job_priority fields when the recruiter leaves these untouched.
  employeeType: z.string().optional().or(z.literal("")),
  currency: z.string().min(1, "Currency is required"),
  currency_sign: z.string(),
  statusFlowId: z.string().min(1, "Candidate status flow is required"),

  vacancyCount: z
    .string()
    .min(1, "Vacancy count is required")
    .regex(NUMBER_REGEX, "Only numbers are allowed")
    .transform((val) => Number(val))
    .refine((val) => val > 0, {
      message: "Vacancy count must be greater than 0",
    })
    .refine((val) => val <= 10000, {
      message: "Vacancy count must not exceed 10000",
    }),

  jobPriority: z.string().optional().or(z.literal("")),

  experienceMin: experienceMinSchema,
  experienceMax: experienceMaxSchema,

  // Structured shift selection \u2014 "predefined" (an Organization Settings
  // shift, by id) or "custom" (job-specific start/end/timezone). "none" is
  // the user picking "No shift", which clears whatever the job held; blank
  // is the picker never being touched, which leaves the job's shift alone.
  // The two are different on the wire \u2014 see buildShiftPayload in
  // services/jobService.js. The actual required-field + start<end + timezone
  // rules run in the schema-level superRefine below via
  // validateJobShiftSelection, shared with the Organization Settings
  // predefined-shift modal (see ./shift.schema.js).
  shiftType: z.union([z.literal("predefined"), z.literal("custom"), z.literal("none"), z.literal("")]).optional(),
  predefinedShiftId: z.string().optional().or(z.literal("")),
  shiftStartTime: z.string().optional().or(z.literal("")),
  shiftEndTime: z.string().optional().or(z.literal("")),
  shiftTimezone: z.string().optional().or(z.literal("")),

  // Optional tenant calendar tag (Organization Settings). Unconditionally
  // optional — "" is the wire value for "No calendar", a real id attaches
  // one. No superRefine needed since there's no combination to validate.
  calendarTypeId: z.string().optional().or(z.literal("")),

  // Skills
  skills: z
    .array(
      z
        .string()
        .trim()
        .min(1, "Skill must not be empty")
        .max(100, "Skill must be 100 characters or fewer") // ← add error message
        .refine(
          (val) => /[a-zA-Z]/.test(val),
          { message: "Skill must not contain only numbers or special characters" }, // ← wrap in object
        ),
    )
    .min(1, "At least one skill is required"),
  // Description
  overview: z.string().min(1, "Role overview is required").min(20, "Overview must be at least 20 characters").max(10000, "Overview must not exceed 10000 characters"),

  responsibility: z
    .string()
    .min(1, "Key responsibilities are required")
    .min(20, "Responsibilities must be at least 20 characters")
    .max(10000, "Responsibilities must not exceed 10000 characters"),

  // Interviewers
  interviewers: z
  .array(
    z.object({
      name: z.string().trim().max(150, "Name too long").optional().or(z.literal("")),
      email: z.string().trim().email("Invalid email address").max(150, "Email must be 150 characters or fewer").optional().or(z.literal("")),
    }),
  )
  .min(1, "At least one interviewer is required")
  .superRefine((interviewers, ctx) => {
    const emails = interviewers.map((i) => i.email?.trim().toLowerCase() ?? "");

    emails.forEach((email, index) => {
      if (!email) return; // skip empty emails
      const firstIndex = emails.indexOf(email);
      if (firstIndex !== index) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, "email"],
          message: "Interviewer already exists",
        });
      }
    });
  }),

  // Organization (Optional)
  department: z.string().trim().max(100, "Department must be 100 characters or fewer").optional().or(z.literal("")),

  division: z.string().trim().max(100, "Division must be 100 characters or fewer").optional().or(z.literal("")),

  // Company Links(Optional)
  // companyWebsite: z.string().trim().url("Invalid website URL").max(300, "Link too long").optional().or(z.literal("")),
  companyWebsite: z
  .string()
  .trim()
  .max(300, "Link too long")
  .refine(
    (val) => val === "" || (() => {
      try { new URL(val); return true; } catch { return false; }
    })(),
    { message: "Invalid website URL" }
  )
  .optional()
  .or(z.literal("")),

  linkedIn: z
    .string()
    .trim()
    .max(300, "Link too long")
    .refine((val) => val === "" || LINKEDIN_REGEX.test(val), {
      message: "Enter a valid LinkedIn profile URL",
    })
    .optional()
    .or(z.literal("")),

  // Additional Info
  niceToHave: z.string().trim().max(10000, "Too long").optional().or(z.literal("")),

  certifications: z.string().trim().max(10000, "Too long").optional().or(z.literal("")),

  // Language requirements, one row per { language, proficiency } pair —
  // the shared pair schema (utils/languageRequirements.js), optional as a
  // whole since a job needs no languages. The rules and their wording live
  // there so every consumer refuses identically.
  languagesRequired: languagePairRowsSchema(),

  // assignedTo
  assignedTo: z.string().trim().optional().or(z.literal("")),

  // Add these two fields at the bottom of the z.object({...}) in jobFormSchema:

  mandatoryFields: z.array(z.string()).default([]),
  selectedConfigurable: z.array(z.string()).default([]),

  // `.optional()`, not `.default([])`. These two are the only fields whose
  // section can be absent from the form — the server sends no options when the
  // tenant is not entitled to the module behind them — and the difference
  // between "selected nothing" and "was not asked" has to survive as far as
  // the payload. A default would rewrite `undefined` to `[]` here, inside the
  // resolver, and the job would lose its stored selection on save.
  selectedInterviewTypes: z.array(z.string()).optional(),
  selectedBooleanPlatforms: z.array(z.string()).optional(),

  // Job-level email consent, one per flow. Declared here for two reasons, and
  // the first is easy to miss: zodResolver hands react-hook-form the *parsed*
  // output, and an undeclared key is stripped — so without these three lines
  // the switches would never reach onSubmit at all.
  //
  // `.optional()` and not `.default(false)`, the same rule as the two option
  // lists above: a flow whose module is withheld draws no row, its field stays
  // `undefined`, and the payload has to drop the key rather than send `false`.
  // Off is a real answer here — there is no Inherit state — so a default would
  // freeze an answer the user never gave.
  requireApplyEmailConsent: z.boolean().optional(),
  requireBulkEmailConsent: z.boolean().optional(),
  requireReuploadEmailConsent: z.boolean().optional(),
  requireJobfitApplyEmailConsent: z.boolean().optional(),
  // Manual screening questions. Client-side mirror of the server validation
  // in app/api/v2/application_questions — question text required, only the two
  // supported answer types. The server re-validates regardless.
  applicationQuestions: z
    .array(
      z.object({
        id: z.string().optional(),
        question: z
          .string()
          .trim()
          .min(1, "Question text is required")
          .max(200, "Question must be 200 characters or fewer"),
        answerType: z.enum(ANSWER_TYPE_VALUES, {
          errorMap: () => ({ message: "Select an answer type" }),
        }),
        isRequired: z.boolean().default(false),
      }),
    )
    .max(20, "You can add at most 20 questions.")
    .superRefine((items, ctx) => {
      // Reject duplicate question text (case-insensitive, trimmed). The issue
      // is anchored to the offending row so the message renders inline on that
      // question's input. Mirrors validate_question_set on the backend.
      const seen = new Map();
      (items || []).forEach((q, i) => {
        const key = (q?.question || "").trim().toLowerCase();
        if (!key) return;
        if (seen.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, "question"],
            message: "Duplicate question — each question must be unique.",
          });
        } else {
          seen.set(key, i);
        }
      });
    })
    .optional()
    .default([]),

}).superRefine((data, ctx) => validateJobShiftSelection(data, ctx))
  .superRefine((data, ctx) => validateJobExperienceRange(data, ctx));

// The create path, where there is no stored deadline to exempt.
export const jobFormSchema = jobFormSchemaFor(null);

export const step2Schema = z
  .object({
    improvementType: z.string().optional(),
    responseDepth: z.string().min(1, "Response Depth is required"),
    customFocus: z.string().optional(),
    enhancement_tone: z
      .string()
      .min(1, "Enhancement Tone is required"),
    improvement_flexibility: z
      .string()
      .min(1, "Improvement Flexibility is required"),
  })
  .superRefine((data, ctx) => {
    const hasImprovementType =
      !!data.improvementType?.trim() &&
      data.improvementType !== "custom";

    const hasCustomFocus = !!data.customFocus?.trim();

    // Must provide exactly one of the two fields
    if (hasImprovementType === hasCustomFocus) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Choose either an Improvement Type or a Custom Prompt",
        path: ["improvementType"],
      });

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Choose either an Improvement Type or a Custom Prompt",
        path: ["customFocus"],
      });
    }
  });